/**
 * Quando si usa la disposizione da telefono invece di quella da televisore.
 *
 * ## Non basta la piattaforma, e il motivo è un difetto vero
 *
 * `detectPlatform` risponde `androidTv` a **qualunque** agente che contenga «android» — telefoni
 * compresi. Per i suoi scopi va bene: serve a scegliere la mappa dei tasti del telecomando, e un
 * telefono non ne ha nessuno. Ma usarla per decidere la disposizione darebbe a ogni telefono Android
 * l'interfaccia da televisore: menu laterale, fuoco direzionale, bersagli pensati per una freccia.
 * Si installerebbe, si aprirebbe, e sarebbe inutilizzabile.
 *
 * ## Il discriminante è «si tocca», non «si chiama telefono»
 *
 * Due domande, in quest'ordine:
 *
 * 1. **È un televisore?** Tizen e webOS lo sono per definizione; Android lo è quando l'agente non
 *    dice `mobile` — i browser dei telefoni lo scrivono, quelli dei televisori no.
 * 2. Se non lo è, **quanto è largo?**
 *
 * L'ordine non è invertibile, ed è la ragione per cui questa funzione esiste: un televisore non deve
 * **mai** ricevere la disposizione da telefono, nemmeno dichiarando una finestra stretta. Succede —
 * un pannello a 720p con un fattore di scala, un browser di sistema che mente — e il risultato
 * sarebbe una barra di schede da toccare su uno schermo che non si tocca, con il telecomando che non
 * la raggiunge. Un'interfaccia inoperabile è peggio di una scomoda.
 */

/**
 * Sopra questa larghezza si usa la disposizione da televisore.
 *
 * 600 px: il prototipo disegna il telefono a 390, i telefoni grandi arrivano intorno a 430, e un
 * tablet in verticale parte da 768. La soglia sta nel mezzo di quel vuoto, dove non cade nessun
 * apparecchio reale — una soglia che taglia in mezzo a una famiglia di schermi cambierebbe
 * disposizione a modelli quasi identici.
 */
export const PHONE_MAX_WIDTH = 600;

/** Quel che serve a decidere, e nient'altro. */
export interface LayoutSignals {
  readonly userAgent: string;
  /** La larghezza della finestra in pixel CSS, non quella fisica dello schermo. */
  readonly viewportWidth: number;
  /** L'altezza della finestra, per lo stesso motivo: vedi `usesPhoneLayout`. */
  readonly viewportHeight: number;
}

/**
 * Se questo apparecchio è un televisore.
 *
 * `mobile` nell'agente è il discriminante fra un Android da televisore e uno da telefono: i browser
 * dei telefoni lo scrivono, quelli dei televisori no. Non è una regola elegante — è la convenzione
 * che esiste, e l'alternativa sarebbe indovinare dalla dimensione, che è proprio quel che non si può
 * fare per un televisore.
 */
export function isTelevision(userAgent: string): boolean {
  const agent = userAgent.toLowerCase();
  if (agent.includes('tizen') || agent.includes('web0s') || agent.includes('webos')) return true;
  return agent.includes('android') && !agent.includes('mobile');
}

/**
 * Se si usa la disposizione da telefono.
 *
 * ## Il tablet prende quella da televisore, e va detto
 *
 * Sopra i 600 px risponde `false`, quindi un tablet riceve l'interfaccia costruita per frecce e OK.
 * Si usa col dito, perché ogni cosa è anche cliccabile, ma è disegnata per un telecomando: bersagli
 * grandi, una colonna di menu, nessuna barra in basso.
 *
 * Non è una svista ed è un compromesso dichiarato: il prototipo disegna due apparecchi, un
 * televisore e un telefono, e non c'è una terza forma da copiare. Inventarla qui vorrebbe dire
 * decidere a nome di chi disegna. Sta fra le cose aperte, non fra quelle risolte.
 */
export function usesPhoneLayout(signals: LayoutSignals): boolean {
  if (isTelevision(signals.userAgent)) return false;
  /*
    Si guarda il **lato corto**, non la larghezza.

    Un telefono girato in orizzontale è largo 844 px e resta un telefono. Guardando la larghezza,
    ruotarlo faceva cambiare disposizione: l'app passava all'interfaccia da televisore, l'albero dei
    componenti veniva smontato e rimontato, e **il canale ripartiva da capo** — con l'attesa d'avvio,
    una sessione nuova sul gateway e il menu del telecomando al posto dei comandi da toccare. Tre
    difetti che sembravano tre e sono uno.

    Il lato corto non cambia ruotando, ed è la misura che dice davvero che forma ha lo schermo: un
    telefono sta sotto i 600 in entrambi i versi, un tablet parte da 744, un televisore da 1080.

    **Il prezzo, detto:** una finestra di browser bassa e larga — 1200×400 — adesso riceve la
    disposizione da telefono, dove prima riceveva quella da televisore. È il caso raro, e fra i due è
    anche il meno peggio: l'interfaccia da telecomando dentro 400 px d'altezza non ci sta.
  */
  const lato = Math.min(signals.viewportWidth, signals.viewportHeight);
  return lato > 0 && lato <= PHONE_MAX_WIDTH;
}
